import { PHASES } from "./model"
import type { ResponseGrid } from "./model"

/**
 * The raw crosstab — Pam's "true-true matrix": for each (topic phase,
 * option phase) pair, the sum of the three direct responses to it, one
 * from each of that phase's three topics.
 *
 * This is the honest measurement, and it is the reason the interference
 * grid exists: three values per cell is too thin to read, so
 * `√(rowΣ × colΣ) − mean` was invented to borrow body from the whole
 * row and column. Having both side by side is what lets the
 * approximation be checked against what it approximates.
 *
 * Rows and columns are in canonical phase order, matching ResultMatrix.
 * Grouping is by `row.phase` rather than by position: the backend's
 * three-set ordering is what makes the sum expressible as a formula in
 * the exported workbook, but the frontend should not silently produce
 * nonsense if that ordering ever changes.
 */
export default function crosstab(responses: ResponseGrid): number[][] {
  return PHASES.map((phase) => {
    const rows = responses.rows.filter((row) => row.phase === phase)

    return PHASES.map((_, column) =>
      rows.reduce((sum, row) => {
        const value = row.values[column]
        // Not ensure(): that is a truthiness check, and 0 is both a
        // valid answer and the scale's neutral default — far and away
        // the commonest response there is.
        if (value === undefined) {
          throw new Error(
            `Response row ${row.topicSlug} has no value for column ${column}`,
          )
        }

        return sum + value
      }, 0),
    )
  })
}
